import Typography from 'apollo-react/components/Typography';
import _ from 'lodash';
import React from 'react';
import { connect } from 'react-redux';

import CompareRevisionsLinkCell from './CompareRevisionsLinkCell';
import SignUpButtonCell from './SignUpButtonCell';
import { compareStrings, createCustomCell, createStringSearchFilter } from './utils';
import { SAVED_SEARCHES_ROUTES_WITH_TIMESTAMP } from '../../constants/SavedSearches';
import { getSavedSearchResultsKey } from '../../reducers/SavedSearchResultsReducer';
import { getPagePathname } from '../../utils/childrenWindowsUtils';
import SearchDataTable from '../SearchDataTable';
import { getFilterComponentByDataType } from '../TableFilters';
import AutoCompleteFilter from '../TableFilters/AutoCompleteFilter';

const ResultsTable = ({
  entityName,
  searchName,
  loadData,
  dataColumns = [],
  translatedSearchName,
  rows,
  size = 0,
  fetching,
  enableSorting = false,
  initialSortedColumn,
  initialSortOrder,
  advancedSearchCriteria,
  initialTableFilters,
  onLoadData,
  useSearchNameAsTitle,
  initialTranslatedSearchName,
  showFilters,
  saveFilters,
  enableSaving,
  savedSearchFilters,
  initialPage,
  setClearAllTableData,
  shouldClearAllTableData,
  ...otherProps
}) => {
  const tableProps = _.pick(otherProps, [
    'title',
    'subtitle',
    'dataKey',
    'rowsPerPageOptions',
    'customCells',
    'CustomHeader',
    'active',
    'onDataChange',
    'extraColumns',
    'customFilter',
    'referencedColumns',
    'dynamicParamCollection',
    'customCell',
    'tablePaginationProps',
    'metadata',
    'emptySectionMessage',
  ]);

  if (useSearchNameAsTitle) {
    tableProps.title = <Typography>{translatedSearchName}</Typography>;
  }

  if (tableProps.customCell && rows && rows.length) {
    if (!tableProps.customCells) {
      tableProps.customCells = [];
    }

    tableProps.customCells.push({
      accessor: _.head(rows).NavigationColumn,
      customCell: tableProps.customCell,
      fixedWidth: true,
    });
  }

  const sortProps = enableSorting
    ? {
        initialSortedColumn,
        initialSortOrder,
      }
    : {};

  return (
    <SearchDataTable
      entityName={entityName}
      searchName={searchName}
      loadData={loadData}
      columns={dataColumns}
      rows={rows}
      size={size}
      isLoading={fetching}
      advancedSearchCriteria={advancedSearchCriteria}
      initialTranslatedSearchName={initialTranslatedSearchName}
      initialTableFilters={initialTableFilters}
      onLoadData={onLoadData}
      showFilters={showFilters}
      saveFilters={saveFilters}
      enableSaving={enableSaving}
      savedSearchFilters={savedSearchFilters}
      initialPage={initialPage}
      setClearAllTableData={setClearAllTableData}
      shouldClearAllTableData={shouldClearAllTableData}
      {...tableProps}
      {...sortProps}
    />
  );
};

const mapStateToProps = (state, ownProps) => {
  const { entityName, searchName, dataKey, shouldCompareRevisions, showSignUpButton } = ownProps;
  const { isOnline } = state.networkStatus;
  const savedSearchedKey = getSavedSearchResultsKey(entityName, searchName, dataKey);
  const resultsState = isOnline ? state.savedSearchResults[savedSearchedKey] ?? {} : {};

  const { translations } = state;
  const { results, fetching } = resultsState;

  if (_.isNil(results)) {
    return { fetching };
  }

  const rows = results.rows ?? [];
  const columns = results.columns ?? [];
  const size = results.totalResultCount ?? 0;
  const translatedSearchName = results.translatedSearchName ?? null;
  const requiresTimeStamp = SAVED_SEARCHES_ROUTES_WITH_TIMESTAMP.includes(getPagePathname());

  const dataColumns = columns.map((column) => ({
    ...column,
    sortFunction: ownProps.enableSorting && compareStrings,
    filterComponent: column.translateData
      ? AutoCompleteFilter
      : getFilterComponentByDataType(column.dataType),
    filterFunction: createStringSearchFilter(column.accessor),
    customCell: createCustomCell(column, translations, requiresTimeStamp),
  }));

  if (shouldCompareRevisions) {
    dataColumns.unshift({
      header: '',
      accessor: 'COMPARE',
      customCell: CompareRevisionsLinkCell,
    });
  }

  if (showSignUpButton) {
    dataColumns.push({
      header: '',
      accessor: 'SIGN_UP',
      customCell: SignUpButtonCell,
    });
  }

  return {
    rows,
    size: parseInt(size, 10),
    fetching,
    dataColumns,
    translatedSearchName,
  };
};

const mapDispatchToProps = {};

export default connect(mapStateToProps, mapDispatchToProps)(ResultsTable);
